/**
 * High-Performance Logging System
 * 
 * Features:
 * - Multiple log levels (DEBUG, INFO, WARN, ERROR)
 * - Async batch processing for performance
 * - Circular buffer for memory efficiency
 * - Structured logging with metadata
 * - Console and storage output
 * - Real-time performance monitoring
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  metadata?: Record<string, any>;
  sessionId: string;
  userId?: string;
}

export interface LoggerConfig {
  level: LogLevel;
  maxBufferSize: number;
  flushInterval: number;
  enableConsole: boolean;
  enableStorage: boolean;
  maxStorageEntries: number;
}

// Callback type for sending logs to UI notification system
type NotificationCallback = (level: string, category: string, message: string, details?: any) => void;

class HighPerformanceLogger {
  private config: LoggerConfig;
  private buffer: LogEntry[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private isProcessing = false;
  private notificationCallback: NotificationCallback | null = null;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      level: LogLevel.DEBUG,
      maxBufferSize: 1000,
      flushInterval: 5000, // 5 seconds
      enableConsole: true,
      enableStorage: true,
      maxStorageEntries: 10000,
      ...config
    };

    this.sessionId = this.generateSessionId();
    this.startFlushTimer();

    // Register cleanup on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  private createLogEntry(
    level: LogLevel,
    category: string,
    message: string,
    metadata?: Record<string, any>
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      metadata,
      sessionId: this.sessionId,
      userId: this.getCurrentUserId()
    };
  }

  private getCurrentUserId(): string | undefined {
    // TODO: Implement user ID retrieval from auth context
    return 'user_001'; // Placeholder
  }

  private async addToBuffer(entry: LogEntry): Promise<void> {
    this.buffer.push(entry);

    // Send to notification system if callback is registered
    if (this.notificationCallback) {
      const levelMap: Record<LogLevel, string> = {
        [LogLevel.DEBUG]: 'info',
        [LogLevel.INFO]: 'info',
        [LogLevel.WARN]: 'warning',
        [LogLevel.ERROR]: 'error'
      };
      this.notificationCallback(
        levelMap[entry.level],
        entry.category,
        entry.message,
        entry.metadata
      );
    }

    // Maintain buffer size limit
    if (this.buffer.length > this.config.maxBufferSize) {
      this.buffer = this.buffer.slice(-this.config.maxBufferSize);
    }

    // Force flush if buffer is getting full
    if (this.buffer.length >= this.config.maxBufferSize * 0.9) {
      await this.flush();
    }
  }

  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  public async flush(): Promise<void> {
    if (this.isProcessing || this.buffer.length === 0) {
      return;
    }

    this.isProcessing = true;
    const entriesToProcess = [...this.buffer];
    this.buffer = [];

    try {
      await Promise.all([
        this.config.enableConsole ? this.flushToConsole(entriesToProcess) : Promise.resolve(),
        this.config.enableStorage ? this.flushToStorage(entriesToProcess) : Promise.resolve()
      ]);
    } catch (error) {
      console.error('Logger flush error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  private async flushToConsole(entries: LogEntry[]): Promise<void> {
    entries.forEach(entry => {
      const logMethod = this.getConsoleMethod(entry.level);
      const timestamp = new Date(entry.timestamp).toLocaleTimeString();
      const metadataStr = entry.metadata ? JSON.stringify(entry.metadata) : '';

      logMethod(
        `[${timestamp}] [${LogLevel[entry.level]}] [${entry.category}] ${entry.message}`,
        metadataStr ? `\nMetadata: ${metadataStr}` : ''
      );
    });
  }

  private getConsoleMethod(level: LogLevel): (...args: any[]) => void {
    switch (level) {
      case LogLevel.DEBUG:
        return console.debug;
      case LogLevel.INFO:
        return console.info;
      case LogLevel.WARN:
        return console.warn;
      case LogLevel.ERROR:
        return console.error;
      default:
        return console.log;
    }
  }

  private async flushToStorage(entries: LogEntry[]): Promise<void> {
    try {
      const storageKey = 'trading_app_logs';
      const existingLogs = JSON.parse(localStorage.getItem(storageKey) || '[]');

      const allLogs = [...existingLogs, ...entries];

      // Maintain storage limit
      const logsToKeep = allLogs.slice(-this.config.maxStorageEntries);

      localStorage.setItem(storageKey, JSON.stringify(logsToKeep));
    } catch (error) {
      console.warn('Failed to save logs to storage:', error);
    }
  }

  // Public logging methods
  public debug(category: string, message: string, metadata?: Record<string, any>): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const entry = this.createLogEntry(LogLevel.DEBUG, category, message, metadata);
      this.addToBuffer(entry);
    }
  }

  public info(category: string, message: string, metadata?: Record<string, any>): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const entry = this.createLogEntry(LogLevel.INFO, category, message, metadata);
      this.addToBuffer(entry);
    }
  }

  public success(category: string, message: string, metadata?: Record<string, any>): void {
    // Success logs are sent ONLY as SUCCESS notifications, NOT as INFO level logs
    // This prevents duplicate logs in Activity Logs panel
    if (this.shouldLog(LogLevel.INFO)) {
      // Send directly to notification system without creating INFO entry
      if (this.notificationCallback) {
        this.notificationCallback('success', category, message, metadata);
      }
    }
  }

  public warn(category: string, message: string, metadata?: Record<string, any>): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const entry = this.createLogEntry(LogLevel.WARN, category, message, metadata);
      this.addToBuffer(entry);
    }
  }

  public warning(category: string, message: string, metadata?: Record<string, any>): void {
    // Alias for warn to match notification context expectations
    this.warn(category, message, metadata);
  }

  public error(category: string, message: string, metadata?: Record<string, any>): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const entry = this.createLogEntry(LogLevel.ERROR, category, message, metadata);
      this.addToBuffer(entry);
    }
  }

  // Performance monitoring
  public startTimer(category: string, operationName: string): () => void {
    const startTime = performance.now();

    return () => {
      const duration = performance.now() - startTime;
      this.info(category, `Operation completed: ${operationName}`, {
        duration: `${duration.toFixed(2)}ms`,
        operation: operationName
      });
    };
  }

  // API call logging
  public logApiCall(
    method: string,
    url: string,
    requestData?: any,
    responseData?: any,
    duration?: number,
    error?: string
  ): void {
    const metadata = {
      method,
      url,
      requestData: requestData ? JSON.stringify(requestData).substring(0, 500) : undefined,
      responseData: responseData ? JSON.stringify(responseData).substring(0, 500) : undefined,
      duration: duration ? `${duration.toFixed(2)}ms` : undefined,
      error
    };

    if (error) {
      this.error('API', `API call failed: ${method} ${url}`, metadata);
    } else {
      this.info('API', `API call: ${method} ${url}`, metadata);
    }
  }

  // Order-specific logging
  public logOrder(action: string, orderData: any, result?: any, error?: string): void {
    // OPTIMIZED: Defer heavy JSON.stringify to background
    // Only store essential fields during critical order path
    const metadata = {
      action,
      orderDataSummary: {
        instrument: orderData.instrument,
        side: orderData.side,
        quantity: orderData.quantity
      },
      resultSummary: result?.orderId ? { orderId: result.orderId } : undefined,
      error,
      timestamp: new Date().toISOString()
    };

    if (error) {
      this.error('ORDER', `Order ${action} failed`, metadata);
    } else {
      this.info('ORDER', `Order ${action} successful`, metadata);
    }
  }

  // Get logs for debugging
  public getLogs(level?: LogLevel, category?: string): LogEntry[] {
    const storageKey = 'trading_app_logs';
    const logs: LogEntry[] = JSON.parse(localStorage.getItem(storageKey) || '[]');

    return logs.filter(log => {
      if (level !== undefined && log.level < level) return false;
      if (category && log.category !== category) return false;
      return true;
    });
  }

  // Clear logs
  public clearLogs(): void {
    localStorage.removeItem('trading_app_logs');
    this.buffer = [];
  }

  // Configuration updates
  public updateConfig(newConfig: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.startFlushTimer();
  }

  // Set notification callback for UI integration
  public setNotificationCallback(callback: NotificationCallback | null): void {
    this.notificationCallback = callback;
  }

  // Cleanup
  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
  }
}

// Create singleton instance
const logger = new HighPerformanceLogger({
  level: process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO,
  enableConsole: true,
  enableStorage: true,
  maxBufferSize: 1000,
  flushInterval: 3000
});

// Export singleton and class for flexibility
export { HighPerformanceLogger, logger };
export default logger;